import type { StaffRole } from "@lafayette/auth";
import { useMemo } from "react";

import { type CapabilityRules, capabilityFlags } from "../access/capabilities";
import { useStaffRoles } from "../access/use-staff-roles";

// What a staff member may do in the diagnostics area. Presentation only: the API decides every request again, so a hidden button is never the
// control. The roles come from the domain's `capabilityRoles`.

export type DiagnosticsCapabilities = Readonly<{
  /** `operations.jobs.read`. Without it the area is not offered. */
  read: boolean;
  /** `operations.jobs.manage`: re-run, pause, resume. */
  manage: boolean;
  /** `audit.read`. */
  audit: boolean;
  /** `payment.reconcile`. */
  reconcile: boolean;
  /** `payment.resolve_exception`. */
  resolveException: boolean;
}>;

const diagnosticsCapabilityRules = {
  read: "operations.jobs.read",
  manage: "operations.jobs.manage",
  audit: "audit.read",
  reconcile: "payment.reconcile",
  resolveException: "payment.resolve_exception",
} as const satisfies CapabilityRules;

export function diagnosticsCapabilitiesForRoles(
  roles: readonly StaffRole[],
): DiagnosticsCapabilities {
  return capabilityFlags(diagnosticsCapabilityRules, roles);
}

export function useDiagnosticsCapabilities(): Readonly<{
  capabilities: DiagnosticsCapabilities;
  isLoading: boolean;
}> {
  const { roles, isLoading } = useStaffRoles();
  const capabilities = useMemo(() => diagnosticsCapabilitiesForRoles(roles), [roles]);
  return { capabilities, isLoading };
}
